// Serialises read-modify-write cycles that share a key.
//
// The session record has two independent writers: the tap path in the UI and
// the sync path draining the queue. Both do `load -> compute -> save` with an
// await in the gap, so without this the later save clobbers the earlier one.
//
// Observed in a live run on 2026-09-10, not hypothesised: a drain read the
// session, a tap closed a block and saved, then the drain saved its own older
// snapshot in which the block was still open. The close vanished from the
// queue and the block stayed open in the database forever, sitting behind a
// later block that had already closed. Neither writer saw an error, because
// each of their own writes had succeeded.

const chains = new Map<string, Promise<unknown>>();

/** Runs `fn` only once every earlier call for the same key has settled. */
export function withLock<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = chains.get(key) ?? Promise.resolve();
  // Runs whether or not the previous holder failed. One failed mutation must
  // not wedge the queue for the rest of a shift.
  const next = prev.then(fn, fn);
  chains.set(key, next.catch(() => undefined));
  return next;
}
